/**
 * Контрол ставки: заголовок, значение, кнопки +/-
 */

import * as PIXI from 'pixi.js';
import { TextStyle } from 'pixi.js';
import { NeumorphicCircleButton } from './NeumorphicCircleButton';
import { COLORS } from '@/config/colors';
import { BET_CONFIG } from '@/config/betConfig';
import type { Lang } from '@/utils/storage';

const TEXTS = {
  ru: { betAmount: 'Сумма ставки', info: 'i' },
  en: { betAmount: 'Bet amount', info: 'i' },
};

export class BetControl extends PIXI.Container {
  private title: PIXI.Text;
  private value: PIXI.Text;
  private minusBtn: NeumorphicCircleButton;
  private plusBtn: NeumorphicCircleButton;
  private currentBet = BET_CONFIG.default;
  private onChangeCallback?: (bet: number) => void;

  constructor() {
    super();
    const btnSize = 44;
    const gap = 44; // Расстояние между кнопками и текстом ставки (расширено)
    const totalWidth = btnSize + gap + 100 + gap + btnSize;
    const centerX = totalWidth / 2;
    const rowY = 22;
    const textCenterX = centerX - btnSize / 2;

    this.title = new PIXI.Text(
      TEXTS.ru.betAmount,
      new TextStyle({
        fontFamily: '"Jost"',
        fontSize: 14,
        fill: COLORS.textPanel,
      })
    );
    this.title.anchor.set(0.5);
    this.title.x = textCenterX;
    this.title.y = 0;
    this.addChild(this.title);

    this.value = new PIXI.Text(
      `$${this.currentBet.toFixed(2)}`,
      new TextStyle({
        fontFamily: '"Jost"',
        fontSize: 28,
        fill: COLORS.textPanel,
        fontWeight: 'bold',
      })
    );
    this.value.anchor.set(0.5);
    this.value.x = textCenterX;
    this.value.y = rowY;
    this.addChild(this.value);

    this.minusBtn = new NeumorphicCircleButton('−', btnSize);
    this.minusBtn.x = textCenterX - gap - this.value.width / 2 - btnSize / 2;
    this.minusBtn.y = rowY;
    this.minusBtn.on('pointerdown', () => this.decrease());
    this.addChild(this.minusBtn);

    this.plusBtn = new NeumorphicCircleButton('+', btnSize);
    this.plusBtn.x = textCenterX + gap + this.value.width / 2 + btnSize / 2;
    this.plusBtn.y = rowY;
    this.plusBtn.on('pointerdown', () => this.increase());
    this.addChild(this.plusBtn);
  }

  getBet(): number {
    return this.currentBet;
  }

  setBet(bet: number): void {
    const clamped = Math.max(
      BET_CONFIG.min,
      Math.min(BET_CONFIG.max, bet)
    );
    const changed = this.currentBet !== clamped;
    this.currentBet = clamped as typeof BET_CONFIG.default;
    this.value.text = `$${this.currentBet.toFixed(2)}`;
    const btnSize = 44;
    const gap = 44;
    const totalWidth = btnSize + gap + 100 + gap + btnSize;
    const textCenterX = totalWidth / 2 - btnSize / 2;
    this.minusBtn.x = textCenterX - gap - this.value.width / 2 - btnSize / 2;
    this.plusBtn.x = textCenterX + gap + this.value.width / 2 + btnSize / 2;
    this.updateButtons();
    if (changed) this.onChangeCallback?.(this.currentBet);
  }

  private increase(): void {
    this.setBet(this.currentBet + BET_CONFIG.step);
  }

  private decrease(): void {
    this.setBet(this.currentBet - BET_CONFIG.step);
  }

  setEnabled(enabled: boolean): void {
    this.minusBtn.setEnabled(enabled);
    this.plusBtn.setEnabled(enabled);
  }

  setOnChange(callback: (bet: number) => void): void {
    this.onChangeCallback = callback;
  }

  private updateButtons(): void {
    this.minusBtn.setEnabled(this.currentBet > BET_CONFIG.min);
    this.plusBtn.setEnabled(this.currentBet < BET_CONFIG.max);
  }

  setLang(lang: Lang): void {
    this.title.text = TEXTS[lang].betAmount;
  }
}
